import React from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import Input from "../../../component-lib/Atoms/Input/Input";
import Button from "../../../component-lib/Molecules/button/Button";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import { ADD_NEW_ROOM_DATA, ROOM_STATUS_OPTIONS } from "../constants";
import { VALIDATION_CONSTANTS } from "../../../utils/validation-constants/validationConstants";
import BootstrapDialog from "../../../utils/bootstrap-dialog-style/BootstrapDialog";

interface AddRoomDialogProps {
  isOpen: boolean;
  defaultRoomDataRow: any;
  dialogHeading: string;
  handleData: (data: any) => void;
  handleClose: () => void;
}

export default function AddNewRoomDialog({
  isOpen = false,
  defaultRoomDataRow,
  dialogHeading = "Your Heading Here...",
  handleClose,
  handleData,
}: AddRoomDialogProps) {
  const validationSchema = Yup.object().shape({
    [ADD_NEW_ROOM_DATA?.ROOM_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.ACCEPT_CHAR_AND_NUMBER,
        "Room name must contain only letters and numbers and must not exceed 40 characters."
      )
      .required("Room name is required"),
    [ADD_NEW_ROOM_DATA?.ROOM_NUMBER]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.LIMIT_20,
        "Room number must not exceed 20 characters."
      )
      .required("Room number is required"),
    [ADD_NEW_ROOM_DATA?.CAPACITY]: Yup.string()
      .matches(VALIDATION_CONSTANTS.NUMERIC, "Capacity must be a number")
      .required("Capacity is required"),
    [ADD_NEW_ROOM_DATA?.STATUS]: Yup.string().required("Status is required"),
  });

  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
    >
      <Formik
        initialValues={{
          [ADD_NEW_ROOM_DATA?.ROOM_NAME]: `${defaultRoomDataRow?.roomName || ""}`,
          [ADD_NEW_ROOM_DATA?.ROOM_NUMBER]: `${defaultRoomDataRow?.roomNumber || ""}`,
          [ADD_NEW_ROOM_DATA?.CAPACITY]: `${defaultRoomDataRow?.capacity ?? ""}`,
          [ADD_NEW_ROOM_DATA?.STATUS]: `${defaultRoomDataRow?.status || "Active"}`,
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          try {
            handleData(values);
          } catch (error) {
            console.log(error);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          values,
          touched,
          setFieldValue,
          handleBlur,
          handleSubmit,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="customized-dialog-title"
              >
                {dialogHeading}{" "}
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />
              {/* MAIN CONTENT */}
              <DialogContent>
                <div
                  style={{
                    overflow: "auto",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  {/* ROOM NAME */}
                  <div className="pt-4">
                    <Input
                      textFieldLabel="Room Name"
                      size="small"
                      name={ADD_NEW_ROOM_DATA.ROOM_NAME}
                      value={values[ADD_NEW_ROOM_DATA.ROOM_NAME]}
                      onChangeCb={(e) => {
                        setFieldValue(ADD_NEW_ROOM_DATA.ROOM_NAME, e.target.value);
                      }}
                      onBlur={handleBlur}
                      error={
                        touched[ADD_NEW_ROOM_DATA.ROOM_NAME] &&
                        Boolean(errors[ADD_NEW_ROOM_DATA.ROOM_NAME])
                      }
                      helperText={
                        touched[ADD_NEW_ROOM_DATA.ROOM_NAME]
                          ? errors[ADD_NEW_ROOM_DATA.ROOM_NAME]
                          : undefined
                      }
                    />
                  </div>

                  {/* ROOM NUMBER */}
                  <div className="pt-4">
                    <Input
                      textFieldLabel="Room Number"
                      size="small"
                      name={ADD_NEW_ROOM_DATA.ROOM_NUMBER}
                      value={values[ADD_NEW_ROOM_DATA.ROOM_NUMBER]}
                      onChangeCb={(e) => {
                        setFieldValue(
                          ADD_NEW_ROOM_DATA.ROOM_NUMBER,
                          e.target.value
                        );
                      }}
                      onBlur={handleBlur}
                      error={
                        touched[ADD_NEW_ROOM_DATA.ROOM_NUMBER] &&
                        Boolean(errors[ADD_NEW_ROOM_DATA.ROOM_NUMBER])
                      }
                      helperText={
                        touched[ADD_NEW_ROOM_DATA.ROOM_NUMBER]
                          ? errors[ADD_NEW_ROOM_DATA.ROOM_NUMBER]
                          : undefined
                      }
                    />
                  </div>

                  {/* CAPACITY */}
                  <Input
                    textFieldLabel="Capacity"
                    type="number"
                    size="small"
                    name={ADD_NEW_ROOM_DATA.CAPACITY}
                    value={values[ADD_NEW_ROOM_DATA.CAPACITY]}
                    onChangeCb={(e) => {
                      setFieldValue(ADD_NEW_ROOM_DATA.CAPACITY, e.target.value);
                    }}
                    onBlur={handleBlur}
                    error={
                      touched[ADD_NEW_ROOM_DATA.CAPACITY] &&
                      Boolean(errors[ADD_NEW_ROOM_DATA.CAPACITY])
                    }
                    helperText={
                      touched[ADD_NEW_ROOM_DATA.CAPACITY]
                        ? errors[ADD_NEW_ROOM_DATA.CAPACITY]
                        : undefined
                    }
                  />

                  {/* STATUS */}
                  <SelectDropdown
                    label="Status"
                    value={values[ADD_NEW_ROOM_DATA.STATUS]}
                    options={ROOM_STATUS_OPTIONS}
                    onChangeCb={(e) =>
                      setFieldValue(ADD_NEW_ROOM_DATA.STATUS, e.target.value)
                    }
                    onBlur={handleBlur}
                    error={
                      touched[ADD_NEW_ROOM_DATA.STATUS] &&
                      Boolean(errors[ADD_NEW_ROOM_DATA.STATUS])
                    }
                    helperText={
                      touched[ADD_NEW_ROOM_DATA.STATUS]
                        ? errors[ADD_NEW_ROOM_DATA.STATUS]
                        : undefined
                    }
                  />
                </div>
              </DialogContent>

              {/* SAVE BUTTON */}
              <DialogActions style={{ padding: "24px" }}>
                <Button
                  type="submit"
                  label={TEXT_CONSTANTS?.SAVE}
                  variant="contained"
                  onClickCb={() => {}}
                  style={{
                    padding: "8px 55px",
                    fontSize: "16px",
                  }}
                />
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </BootstrapDialog>
  );
}
